import { NgClass } from '@angular/common';
import { Component, OnDestroy, effect, inject, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  InfiniteScrollCustomEvent,
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonFab,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonMenuButton,
  IonPopover,
  IonRouterLink,
  IonSearchbar,
  IonText,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';

import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';

import { PwaInstallButtonComponent } from '@libs/core/lib/pwa/pwa-install-button.component';
import { PWAInstallStore } from '@libs/core/lib/pwa/pwa-install.store';
import { VxBlurImgComponent } from '@libs/core/lib/shared/mtjr-blur-image.component';
import { VxDynamicImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { dataFromState } from '@libs/core/lib/state/async-states';
import {
  withGetVendorById,
  withVendorsListForVendors,
} from '@libs/mtjr/shared/generated/entities/vendor/vendor-store-features';
import { PublishStatusList } from '@libs/mtjr/shared/generated/types';
import { signalStore, withComputed } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
export const VendorStore = signalStore(
  {
    providedIn: 'root',
  },
  withVendorsListForVendors(),
  withGetVendorById(),
  withComputed((store) => ({
    vendor: dataFromState(store.getVendorByIdState),
  })),
);

@Component({
  selector: 'mav-store',
  imports: [
    IonLabel,
    IonButtons,
    IonMenuButton,
    IonIcon,
    IonContent,
    IonToolbar,
    RouterLink,
    IonRouterLink,
    IonCard,
    IonHeader,
    IonTitle,
    IonButton,
    IonFab,
    IonSearchbar,
    IonText,
    NgClass,
    IonItem,
    VxLoadingComponent,
    VxErrorComponent,
    IonPopover,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    TranslatePipe,
    PwaInstallButtonComponent,
    VxDynamicImageComponent,
    VxBlurImgComponent,
  ],
  templateUrl: './vendors.page.html',
})
export class VendorsPage implements OnDestroy {
  modalCtr = inject(ModalController);
  vendorPublishStatuses = PublishStatusList;
  store = inject(VendorStore);
  subscription?: Subscription;
  router = inject(Router);
  eventBus = inject(NgEventBus);
  searchValue = signal<string | undefined>(undefined);
  route = inject(ActivatedRoute);
  ionPopover = viewChild(IonPopover, { read: IonPopover });
  pwaInstallStore = inject(PWAInstallStore);
  constructor() {
    this.subscription = this.eventBus.on('vendors:fetch').subscribe(() => this.store.vendorsListForVendorsPage());

    effect(() => {
      const query = this.store.vendorsListForVendorsInput();
      this.router.navigate([], {
        queryParams: {
          publishStatus: query.publishStatus,
          search: query.search ? query.search : undefined,
        },
      });
    });

    this.route.queryParams.subscribe((params) => {
      const { publishStatus, search } = params;
      this.searchValue.set(search);
      const query = this.store.vendorsListForVendorsInput();
      if (
        this.store.vendorsListForVendorsState().type !== 'initial' &&
        publishStatus === query.publishStatus &&
        search === query.search
      )
        return;

      this.store.vendorsListForVendorsPage({ publishStatus, search });
    });
  }

  async createVendor() {
    this.router.navigate(['main', 'vendors', 'create']);
  }

  ionViewWillLeave() {
    this.ionPopover()?.dismiss();
    this.searchValue.set(undefined);
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async onIonInfinite(event: InfiniteScrollCustomEvent) {
    await this.store.vendorsListForVendorsFetchNextPage();
    await event?.target?.complete();
  }

  handleSearchInput(event?: CustomEvent) {
    const search = event?.detail?.value;
    if (this.store.vendorsListForVendorsInput().search === search) return;
    this.store.vendorsListForVendorsPage({ search, page: 1 });
  }
}
